import Procedure from '../../Procedure';
import {
    InvoiceModel,
    PreparedDataOnInvoiceDelete,
} from '../../../../types/Invoice';
import { EntryModel } from '../../../../types/Entries';
import { EntryProcedureModel } from '../../../../types/EntryProcedure';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import InvoiceInvoicingProduct from '../../../models/InvoiceInvoicingProduct';
import { toPreparedDataItems } from './common';

type DeleteInvoiceRelatedData = {
    invoiceElement: InvoiceModel;
    invoiceToDelete: InvoiceModel;
    entriesToUpdate: EntryModel[];
    entryProceduresToUpdate: EntryProcedureModel[];
    productsToDelete: InvoiceInvoicingProduct[];
};

const prepareDeleteInvoice = ({
    invoiceElement,
}: {
    invoiceElement: InvoiceModel;
}) => {
    return invoiceElement.prepareMarkAsDeleted();
};

const prepareDeleteInvoiceEntries = ({
    entries,
}: {
    entries: EntryModel[];
}) => {
    const entriesToUpdate = entries.map((entry) =>
        entry.prepareUpdate((record) => {
            record.invoiceId = null;
        }),
    );

    return { entriesToUpdate };
};

const fetchDeleteInvoiceEntryProceduresData = async ({
    entries,
    procedureService,
}: {
    entries: EntryModel[];
    procedureService: Procedure;
}) => {
    const entryProcedurePairs: {
        entryProc: EntryProcedureModel;
        procedure: Awaited<ReturnType<Procedure['getByID']>>;
    }[] = [];

    await Promise.all(
        entries.map(async (entry) => {
            const entryProcedures = await entry.entryProcedures.fetch();

            await Promise.all(
                entryProcedures.map(async (entryProc) => {
                    const procedure = await procedureService.getByID(
                        entryProc.procedureId,
                    );

                    entryProcedurePairs.push({ entryProc, procedure });
                }),
            );
        }),
    );

    return entryProcedurePairs;
};

const prepareDeleteInvoiceEntryProcedures = (
    entryProcedurePairs: Awaited<
        ReturnType<typeof fetchDeleteInvoiceEntryProceduresData>
    >,
) => {
    const entryProceduresToUpdate = entryProcedurePairs.map(
        ({ entryProc, procedure }) =>
            entryProc.prepareUpdate((ep) => {
                ep.name = procedure.name;
                ep.price = procedure.price ?? '';
            }),
    );

    return { entryProceduresToUpdate };
};

const prepareDeleteInvoiceProducts = ({
    products,
}: {
    products: InvoiceInvoicingProduct[];
}) => {
    const productsToDelete = products.map((product) =>
        product.prepareMarkAsDeleted(),
    );

    return { productsToDelete };
};

const prepareDeleteInvoiceRelatedData = ({
    invoiceElement,
    entries,
    products,
    entryProcedurePairs,
}: {
    invoiceElement: InvoiceModel;
    entries: EntryModel[];
    products: InvoiceInvoicingProduct[];
    entryProcedurePairs: Awaited<
        ReturnType<typeof fetchDeleteInvoiceEntryProceduresData>
    >;
}) => {
    const invoiceToDelete = prepareDeleteInvoice({ invoiceElement });
    const { entriesToUpdate } = prepareDeleteInvoiceEntries({ entries });
    const { entryProceduresToUpdate } =
        prepareDeleteInvoiceEntryProcedures(entryProcedurePairs);
    const { productsToDelete } = prepareDeleteInvoiceProducts({ products });

    return {
        invoiceElement,
        invoiceToDelete,
        entriesToUpdate,
        entryProceduresToUpdate,
        productsToDelete,
    };
};

const appendPreparedDeleteInvoice = (
    preparedData: PreparedDataOnInvoiceDelete[],
    deleteData: Pick<DeleteInvoiceRelatedData, 'invoiceToDelete'>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            [deleteData.invoiceToDelete],
            'invoices',
            'delete',
        ),
    ];
};

const appendPreparedDeleteInvoiceEntries = (
    preparedData: PreparedDataOnInvoiceDelete[],
    deleteData: Pick<DeleteInvoiceRelatedData, 'entriesToUpdate'>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(deleteData.entriesToUpdate, 'entries', 'update'),
    ];
};

const appendPreparedDeleteInvoiceEntryProcedures = (
    preparedData: PreparedDataOnInvoiceDelete[],
    deleteData: Pick<DeleteInvoiceRelatedData, 'entryProceduresToUpdate'>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            deleteData.entryProceduresToUpdate,
            'entry_procedures',
            'update',
        ),
    ];
};

const appendPreparedDeleteInvoiceProducts = (
    preparedData: PreparedDataOnInvoiceDelete[],
    deleteData: Pick<DeleteInvoiceRelatedData, 'productsToDelete'>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            deleteData.productsToDelete,
            'invoice_invoicing_products',
            'delete',
        ),
    ];
};

const logDeleteDBActions = ({
    deleteData,
    logDBAction,
    tableName,
}: {
    deleteData: DeleteInvoiceRelatedData;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    logDBAction({
        message: 'Delete invoice',
        modelName: tableName,
        payload: deleteData.invoiceElement,
    });

    logDBAction({
        message: 'Delete invoice - update entries',
        modelName: tableName,
        payload: deleteData.entriesToUpdate,
    });

    logDBAction({
        message: 'Delete invoice - update entry procedures',
        modelName: tableName,
        payload: deleteData.entryProceduresToUpdate,
    });

    logDBAction({
        message: 'Delete invoice - delete invoice invoicing products',
        modelName: tableName,
        payload: deleteData.productsToDelete,
    });
};

export {
    appendPreparedDeleteInvoice,
    appendPreparedDeleteInvoiceEntries,
    appendPreparedDeleteInvoiceEntryProcedures,
    appendPreparedDeleteInvoiceProducts,
    fetchDeleteInvoiceEntryProceduresData,
    logDeleteDBActions,
    prepareDeleteInvoiceRelatedData,
};

export type { DeleteInvoiceRelatedData };
